import { ComponentProps, PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react'

import { useLocation } from 'react-router-dom'

import { useReducer } from '@cash-web/shared-data-access-redux-inspect'
import { JourneyContext, JourneyNavigate, JourneyTarget } from '@cash-web/shared-journey-data-access-context'

import { useGetRouteScopeConfig } from './hooks/useGetRouteScopeConfig'
import { journeyPaneReducer } from './journeyPaneReducer'
import { JourneyPaneRenderer } from './JourneyPaneRenderer'
import { JourneyRouter } from './JourneyRouter'

const IDLE_PATH = '/'

export const JourneyPaneProvider = ({
  children,
  ...props
}: PropsWithChildren<ComponentProps<typeof JourneyRouter>>) => {
  const lastJourneyPath = useRef<string | null>(null)
  const getRouteScopeConfig = useGetRouteScopeConfig({ router: props.router })
  const [state, dispatch] = useReducer(
    journeyPaneReducer,
    {
      history: [],
      isInProgress: false,
    },
    { name: 'JourneyProvider' }
  )

  const navigate = useCallback(
    async (path, opts) => {
      const isIdlePath = path === IDLE_PATH
      const isNumber = typeof path === 'number'
      const canNavigateToPath = lastJourneyPath.current !== path || isNumber || opts?.scope?.['target'] === 'moneybot'

      if (!canNavigateToPath) {
        return
      }
      if (isIdlePath) {
        dispatch({ type: 'JOURNEY_IDLE' })
      } else {
        if (typeof path === 'number' && path < 0) {
          if (state.history.length > 0) {
            dispatch({ type: 'JOURNEY_CANCEL' })
          } else {
            dispatch({ type: 'JOURNEY_IDLE' })
          }
        } else {
          const isJourneyInProgress = state.history.some(h => h.path === path)

          if (isJourneyInProgress) {
            // check to see if the journey is already in progress in the history
            for (let i = state.history.length - 1; i >= 0; i--) {
              const journeyHistory = state.history[i]
              dispatch({ type: 'JOURNEY_COMPLETE' })
              if (journeyHistory.path === path) {
                break
              }
            }
          }

          const scope = getRouteScopeConfig({ path, opts })
          dispatch({ type: 'JOURNEY_START', path, opts: { ...opts, scope: { ...scope, ...opts?.scope } } })
        }
      }
      lastJourneyPath.current = path
    },
    [state.history, getRouteScopeConfig]
  ) as JourneyNavigate

  const cancel = useCallback(
    (journeyId?: string) => {
      dispatch({ type: 'JOURNEY_CANCEL', journeyId })
      lastJourneyPath.current = null
    },
    [dispatch]
  )

  // Close out of all journeys and complete any active flows.
  const cancelAll = useCallback(
    (excludeTargets?: JourneyTarget[]) => {
      dispatch({ type: 'JOURNEY_IDLE', excludeTargets })
      lastJourneyPath.current = null
    },
    [dispatch]
  )

  const complete = useCallback(
    (journeyId?: string) => {
      dispatch({ type: 'JOURNEY_COMPLETE', journeyId })
      lastJourneyPath.current = null
    },
    [dispatch]
  )

  const hasParentTarget = useCallback(
    (target: JourneyTarget) => {
      return state.history.some(h => (h.opts?.scope?.['target'] ?? 'modal') === target)
    },
    [state.history]
  )

  const handleCompleteFlow = useCallback(() => {
    lastJourneyPath.current = null
  }, [])

  const context = useMemo(
    () => ({
      navigate,
      end: complete,
      idle: cancelAll,
      endAll: cancelAll,
      isInProgress: state.isInProgress,
      active: state.history.at(-1),
      hasParentTarget,
    }),
    [navigate, complete, cancelAll, state.isInProgress, state.history, hasParentTarget]
  )

  const location = useLocation()

  // we should cancel all journeys when the user navigates to a different browser path
  useEffect(() => {
    cancelAll(['moneybot', 'inline'])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <JourneyContext.Provider value={context}>
      <JourneyPaneRenderer
        onCompleteFlow={handleCompleteFlow}
        onCancelFlow={cancel}
        onBack={cancel}
        onClose={cancelAll}
        history={state.history}
        {...props}
      >
        {children}
      </JourneyPaneRenderer>
    </JourneyContext.Provider>
  )
}
